/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 *   button[command="--show"][data-media-id] — thumbnail that jumps the gallery to that photo.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #snapTimer = 0;
  #slideAc = null;
  #pendingIndex = null;
  #activeIndex = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--show') {
        this.showMedia(event.source?.dataset.mediaId, 'slide');
        return;
      }
      if (!['--previous', '--next'].includes(event.command)) return;
      const gallery = this.querySelector('[data-product-gallery]');
      if (!gallery || !gallery.children.length) return;
      const slides = Array.from(gallery.children);
      // Index from scroll position, then jump with snapping paused: a relative scrollBy on a
      // mandatory snap container lets browsers snap one slide further, skipping photos.
      const index = this.#pendingIndex ?? Math.round(gallery.scrollLeft / gallery.clientWidth);
      const offset = event.command === '--next' ? 1 : -1;
      const target = (index + offset + slides.length) % slides.length;
      this.showMedia(slides[target].dataset.mediaId, 'slide');
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#pendingIndex = target;
    }, opts);
    // Keep the thumbnail of the visible photo highlighted while shoppers swipe or use the arrows.
    const gallery = this.querySelector('[data-product-gallery]');
    let frame = 0;
    gallery?.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.#markActiveThumb(gallery));
    }, { ...opts, passive: true });
    // Place the wheel without animating, then keep it sized when the thumbnails change size.
    const ring = this.querySelector('[data-gallery-ring]');
    if (ring) {
      ring.classList.add('[&_*]:transition-none!');
      this.#layoutRing();
      requestAnimationFrame(() => requestAnimationFrame(() => ring.classList.remove('[&_*]:transition-none!')));
      const resizeObserver = new ResizeObserver(() => this.#layoutRing());
      resizeObserver.observe(ring);
      this.#ac.signal.addEventListener('abort', () => resizeObserver.disconnect());
    }
    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // Deep links (?variant=) open on the selected variant's image.
    const initialMediaId = this.dataset.initialMediaId;
    if (initialMediaId) requestAnimationFrame(() => this.showMedia(initialMediaId, 'instant'));
  }

  /**
   * Scrolls the gallery to the slide for the given media id.
   * Called by <s-variant-picker> after a variant change.
   */
  showMedia(mediaId, behavior) {
    if (!mediaId) return;
    const gallery = this.querySelector('[data-product-gallery]');
    const slide = gallery?.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!gallery || !slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = slide.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
    const target = gallery.scrollLeft + offset;
    // Mobile Safari can drop programmatic smooth scrolls on mandatory snap containers,
    // so snapping is paused while jumping straight to the variant's slide.
    gallery.style.scrollSnapType = 'none';
    clearTimeout(this.#snapTimer);
    this.#slideAc?.abort();
    this.#pendingIndex = null;
    if (behavior === 'slide' && !reduceMotion) {
      // Arrows and thumbnails glide to the photo; snapping comes back once the scroll settles.
      // Each new glide cancels the previous one's restore so rapid clicks never jump back.
      const ac = new AbortController();
      this.#slideAc = ac;
      gallery.scrollTo({ left: target, behavior: 'smooth' });
      const restore = () => {
        if (ac.signal.aborted) return;
        ac.abort();
        clearTimeout(this.#snapTimer);
        this.#pendingIndex = null;
        gallery.scrollLeft = target;
        gallery.style.scrollSnapType = '';
      };
      gallery.addEventListener('scrollend', restore, { once: true, signal: ac.signal });
      this.#snapTimer = setTimeout(restore, 700);
      return;
    }
    gallery.scrollTo({ left: target, behavior: 'instant' });
    requestAnimationFrame(() => {
      gallery.scrollLeft = target;
      gallery.style.scrollSnapType = '';
    });

    // On phones the gallery sits above the variant picker; bring it back into view after a variant change
    // so shoppers actually see the new image.
    if (behavior !== 'instant' && behavior !== 'slide') {
      const rect = gallery.getBoundingClientRect();
      const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      if (visible < rect.height / 2) {
        const header = document.querySelector('header');
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        window.scrollBy({ top: rect.top - headerHeight - 8, behavior: reduceMotion ? 'instant' : 'smooth' });
      }
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }


  #markActiveThumb(gallery) {
    const slides = Array.from(gallery.children);
    if (!slides.length) return;
    const index = Math.min(slides.length - 1, Math.max(0, Math.round(gallery.scrollLeft / gallery.clientWidth)));
    this.#activeIndex = index;
    this.#layoutRing();
  }

  // Thumbnails sit on the rim of a wheel facing the shopper: the active photo in front, neighbours turned
  // away on both sides and dimmed, the next ones fading out. Photos wrap around in both directions.
  #layoutRing() {
    const ring = this.querySelector('[data-gallery-ring]');
    if (!ring) return;
    const thumbs = Array.from(ring.querySelectorAll('[data-media-id]'));
    const count = thumbs.length;
    if (!count) return;
    const step = 36;
    const width = thumbs[0].offsetWidth || 64;
    const radius = (width * 1.15) / (2 * Math.sin((step / 2) * (Math.PI / 180)));
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const active = this.#activeIndex ?? 0;
    ring.style.transform = `translateZ(${-radius}px)`;

    const looks = [
      { opacity: 1, brightness: 1 },
      { opacity: 0.9, brightness: 0.75 },
      { opacity: 0.55, brightness: 0.5 },
      { opacity: 0.2, brightness: 0.4 },
    ];
    thumbs.forEach((thumb, i) => {
      let offset = (((i - active) % count) + count) % count;
      if (offset > count / 2) offset -= count;
      const previous = Number(thumb.dataset.ringOffset);
      // A thumbnail wrapping to the other side jumps without animating so it never sweeps across the front.
      const wraps = Number.isFinite(previous) && Math.abs(offset - previous) > 1;
      thumb.dataset.ringOffset = String(offset);
      if (wraps) thumb.style.transition = 'none';
      const distance = Math.abs(offset);
      const look = looks[distance] ?? { opacity: 0, brightness: 0.35 };
      thumb.style.transform = `rotateY(${offset * step * rtl}deg) translateZ(${radius}px)`;
      thumb.style.opacity = String(look.opacity);
      thumb.style.filter = distance === 0 ? '' : `brightness(${look.brightness})`;
      if (wraps) requestAnimationFrame(() => { thumb.style.transition = ''; });
      if (distance === 0) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      const reachable = distance <= 2;
      thumb.tabIndex = reachable ? 0 : -1;
      thumb.style.pointerEvents = reachable ? '' : 'none';
      thumb.toggleAttribute('aria-hidden', !reachable);
    });
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
